/**
 * Responsive image helper for products with processed web variants.
 * Maps product slugs to their responsive image sets in /Assets/images/responsive/
 */

// Available responsive variants per product slug → type → widths
const RESPONSIVE_VARIANTS = {
  "2n1-custom-logo-mat": { hero: [600, 1200], thumb: [600] },
  "2n1-grid-entry": { hero: [600], thumb: [600] },
  "astoria-nop-clm": { hero: [600, 1200], thumb: [600] },
  "astoria-nop-rg": { hero: [600, 1200], thumb: [600] },
  "atlas-plus-ctg": { hero: [600, 1200], thumb: [600] },
  "avenue-rib-rg": { hero: [600, 1200], thumb: [600] },
  "axis-rib-rg": { hero: [600, 1200, 1800] },
  "channel-rib-cm": { hero: [600, 1200, 1800], thumb: [600] },
  "chevron-rib-cm": { hero: [600, 1200], thumb: [600] },
  "diplomat-tile": { hero: [600, 1200] },
  "dominator-lp": { hero: [600, 1200] },
  "dominator-tile": { hero: [600, 1200] },
  "frontrunner": { hero: [600, 1200] },
  "frontrunner-plus": { hero: [600, 1200], thumb: [600] },
  "frontrunner-sb": { hero: [600, 1200], thumb: [600] },
  "frontrunner-sb-plus": { hero: [600], thumb: [600] },
  "hercules-mxp": { hero: [600], thumb: [600] },
  "heron-rib-ges": { hero: [600, 1200, 1800] },
  "heron-tile-ges": { hero: [600, 1200, 1800] },
  "pinnacle-hd-custom": { hero: [600, 1200], thumb: [600] },
  "portico-nop-carpet-tile": { hero: [600, 1200], thumb: [600] },
  "portico-nop-customs": { hero: [600], thumb: [600] },
  "portico-nop-roll-goods": { hero: [600, 1200], thumb: [600] },
  "premier-nop-ctg": { hero: [600, 1200, 1800], thumb: [600] },
  "premier-nop-customs-clm": { hero: [600, 1200], thumb: [600, 1200] },
  "premier-nop-tile-rg": { hero: [600, 1200, 1800], thumb: [600] },
  "s-trax-plus": { hero: [600, 1200], thumb: [600] },
  "snap-trax-plus": { hero: [600] },
  "snap-trax-tile": { hero: [600], thumb: [600] },
  "superior-nop-rg": { hero: [600, 1200] },
  "tidal-tidal-diamond-cm": { hero: [600, 1200, 1800] },
};

/**
 * Check if a product slug has responsive variants available
 */
export function hasResponsiveImages(slug) {
  return slug in RESPONSIVE_VARIANTS;
}

/**
 * Get responsive image props for a product hero or thumbnail.
 * Returns { src, srcSet, sizes } for use in <img> tags.
 * Only includes widths that actually have files on disk.
 */
export function getResponsiveImageProps(slug, type = "hero", fallbackSrc = "", sizes = null) {
  const productVariants = RESPONSIVE_VARIANTS[slug];
  if (!productVariants) {
    return { src: fallbackSrc };
  }

  const widths = productVariants[type];
  if (!widths || widths.length === 0) {
    return { src: fallbackSrc };
  }

  const base = `/Assets/images/responsive/${slug}/${slug}_${type}`;

  const srcSetParts = widths
    .map(w => `${base}-${w}.jpg ${w}w`)
    .join(", ");

  // Use the largest available variant as the default src
  const largestWidth = widths[widths.length - 1];

  const defaultSizes = type === "hero"
    ? "(max-width: 768px) 100vw, (max-width: 1400px) 50vw, 1200px"
    : "(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 300px";

  return {
    src: `${base}-${largestWidth}.jpg`,
    srcSet: srcSetParts,
    sizes: sizes || defaultSizes,
  };
}

/**
 * ResponsiveProductImage component
 * Drop-in replacement for <img> that automatically uses srcset when available.
 */
export function ResponsiveProductImage({ slug, type = "hero", fallbackSrc, alt, className, sizes, ...rest }) {
  const props = getResponsiveImageProps(slug, type, fallbackSrc, sizes);

  return (
    <img
      src={props.src}
      srcSet={props.srcSet}
      sizes={props.sizes}
      alt={alt || ""}
      className={className}
      loading="lazy"
      decoding="async"
      {...rest}
    />
  );
}
